import { useState, useEffect } from "react";
import {
  FormLabel,
  Box,
  Card,
  Button,
  Flex,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Input,
} from "@chakra-ui/react";
import { useQuery } from "react-query";
import IssbContainer from "./IssbContainer";
import PageHeader from "components/text/PageHeader";
import templatePackageService from "api/issb/templatepackage.service";
import { useQueryClient } from "react-query";
import { useAuth } from "context";
import FileListWithBrowseOrUpload from "./components/FileListWithBrowseOrUpload";
import { routes } from "config/paths";
import { AButton } from "components/A";
import { Pencil, Trash } from "@phosphor-icons/react";
import { formatDate } from "utils/index";
import DataTableV2 from "components/DataTable/DateTableV2";
import assert from "utils/assert";
import { useConfirmation } from "context/Mutation/ConfirmationDialogContext";
import useAirUserRoles from "utils/AuthUtils";
import A from "components/A";
import PropInput from "components/PropInput";

const queryKey = "template-packages";

/**
 Figma: https://www.figma.com/design/uDql1qJTNBIvGnFDsOpWV4/ESG360-AIR?node-id=3269-18902&t=T2Q9F7ZG6NFSxqLs-0
 
 This area is ESG360-staff-only for now, so design is not crucial.
 
 */
function IssbTemplatePackagePage() {
  const { getAccessTokenSilently } = useAuth();

  const {
    data: packagesPage,
    isLoading,
    error,
  } = useQuery(
    queryKey,
    async () => {
      await getAccessTokenSilently();
      return templatePackageService.list();
    },
    {
      enabled: !!getAccessTokenSilently,
    }
  );

  const packages = packagesPage?.content;

  const [isCreatePackageModalOpen, setIsCreatePackageModalOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);

  /** @type {ColumnDef[]} */
  const columns = [
    {
      header: "Name",
      accessorKey: "name",
      size: 200,
    },
    {
      header: "Tags",
      accessorKey: "tags",
      size: 200,
      cell: ({ row }) => <Text>{row.original.tags?.join(", ")}</Text>,
    },
    // {
    // 	header: "Rounds",
    // 	accessorKey: "TODO",
    // 	size: 200,
    // },
    {
      header: "Status",
      accessorKey: "status",
      size: 100,
    },
    {
      header: "Created date",
      size: 100,
      accessorFn: (file) => formatDate(file.created_date),
    },
    {
      header: "Questions",
      size: 100,
      cell: ({ row }) => (
        <AButton
          alignSelf="flex-start"
          variant="link"
          href={routes.issbTemplateQuestionList + "?packageId=" + row.original.id}
        >
          <Text as="span">Go to questions</Text>
        </AButton>
      ),
    },
  ];

  const { isESGAdmin } = useAirUserRoles();

  return (
    <IssbContainer>
      <Flex direction="column" gap={2}>
        <PageHeader
          title="Frameworks"
          subTitle="Frameworks provide sets of template questions e.g. for a regulation like ISSB"
        />

        {!isESGAdmin && (
          <Text border="1px solid red" p={2} borderRadius={4}>
            You are NOT an ESG Administrator here. This page is only designed for ESG Administrators.
          </Text>
        )}

        {isLoading && <Text>Loading...</Text>}
        {error && <Text>Error: {error.message}</Text>}

        <Button
          mt={4}
          variant="secondary"
          onClick={() => {
            setSelectedPackage(null);
            setIsCreatePackageModalOpen(true);
          }}
        >
          Create new package
        </Button>
        <EditPackageModal
          pkg={selectedPackage}
          isOpen={isCreatePackageModalOpen}
          onClose={() => setIsCreatePackageModalOpen(false)}
        />
        {packagesPage?.content && (
          <DataTableV2
            onRowClick={(_event, row) => {
              setSelectedPackage(row.original);
              setIsCreatePackageModalOpen(true);
            }}
            columns={columns}
            data={packages}
            isLoading={isLoading}
            serverSidePagination={false} // TODO what happens if there are 1000 files?
          />
        )}
      </Flex>
    </IssbContainer>
  );
}

const EditPackageModal = ({ isOpen, onClose, pkg }) => {
  const queryClient = useQueryClient();
  const [localPkg, setLocalPkg] = useState();
  useEffect(() => {
    setLocalPkg({ ...pkg, _tags: pkg?.tags?.join(", ") });
  }, [pkg]);
  if (localPkg && !localPkg.settings) {
    // initialise settings if not present
    localPkg.settings = {};
  }
  const [fileInfoList, setFileInfoList] = useState([]);
  if (pkg?.fileinfoId && !fileInfoList?.length) {
    // TODO fetch fileInfo
    // temp hack: make stub for now to show "something is here"
    const _fileInfoList = [{ id: pkg.fileinfoId, name: pkg.fileinfoId }];
    setFileInfoList(_fileInfoList);
  }
  const handleFileInfoListChange = (newFileInfoList) => {
    setFileInfoList(newFileInfoList);
    console.log("fileInfoList", fileInfoList);
  };
  const handleSave = () => {
    console.log("pkg", pkg, "localPkg", localPkg, "fileInfoList", fileInfoList);
    localPkg.fileinfoId = fileInfoList.map((f) => f.id)[0];
    localPkg.tags = localPkg._tags?.split(",").map((t) => t.trim());
    let p;
    if (pkg?.id) {
      // Note: fileinfoId will not trigger question creation for existing packages, see ESG-2881
      p = templatePackageService.update(pkg.id, localPkg);
    } else {
      p = templatePackageService.create(localPkg);
    }
    p.then((pkg) => {
      queryClient.invalidateQueries(queryKey);
      console.log("saved pkg", pkg);
      onClose();
    });
  };

  const { confirm } = useConfirmation();
  const handleDelete = async () => {
    assert(pkg.id, "Cannot delete package with no id");
    const confirmed = await confirm({
      title: "Delete Framework - Are you sure?",
      message:
        "This action won't delete existing rounds based on this framework, but will prevent new rounds from being created.",
    });
    if (!confirmed) return;
    templatePackageService.delete(pkg.id).then(() => {
      queryClient.invalidateQueries(queryKey);
    });
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent backgroundColor="white">
        <ModalHeader>{pkg?.id ? "Edit" : "Create"} Framework</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {pkg?.id && <Text>ID: {pkg.id}</Text>}
          <FormLabel mt={4}>Framework name</FormLabel>
          <Input
            label="Framework name"
            type="text"
            value={localPkg?.name}
            onChange={(e) => setLocalPkg({ ...localPkg, name: e.target.value })}
          />
          <FormLabel mt={4}>Framework Tags for Managing Access</FormLabel>
          <Input
            label="Tags (to manage access)"
            type="text"
            placeholder="e.g. ISSB, ASRS"
            value={localPkg?._tags || localPkg?.tags?.join(", ")}
            onChange={(e) => setLocalPkg({ ...localPkg, _tags: e.target.value })}
          />
          {pkg?.id /* can only upload file when creating a new package, see ESG-2881 */ ? (
            <Text>Framework file: {fileInfoList[0]?.name || "No file was uploaded"}</Text>
          ) : (
            <FileListWithBrowseOrUpload
              mt={4}
              fileInfoList={fileInfoList}
              onFileInfoListChange={handleFileInfoListChange}
              tags="template-package"
              canUpload={true}
              canBrowse={false}
            />
          )}

          <PropInput
            mt={4}
            label="Answers can have attachments"
            item={localPkg}
            prop="answersCanHaveAttachments"
            type="checkbox"
          />
          <PropInput
            mt={4}
            label="Answers can use rich-text / html"
            item={localPkg}
            prop="answersCanUseHtml"
            type="checkbox"
          />
          <PropInput
            mt={4}
            label="Optional Round Tags (eg CDP themes: ClimateChange, Forests, WaterSecurity)"
            item={localPkg?.settings}
            prop="optionalRoundTags"
            type="text"
            placeholder="Comma-separated list of tags"
          />
          <PropInput
            mt={4}
            label="User explicitly sets Round Sectors"
            help="If checked, user must set sectors when starting the round. If unchecked, sectors are set from the entities in the round."
            item={localPkg?.settings}
            prop="isSetSectorsExplicitly"
            type="checkbox"
          />

          {pkg?.id && (
            <A mt={2} color="limeShade.600" href={routes.issbTemplateQuestionList + "?packageId=" + pkg.id}>
              Go to questions
            </A>
          )}
        </ModalBody>

        <ModalFooter>
          <Button onClick={onClose}>Close</Button>
          <Button ml={2} onClick={handleSave} variant="primary">
            Save
          </Button>
          {pkg?.id && (
            <Button ml={2} onClick={handleDelete} color="red">
              <Trash />
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default IssbTemplatePackagePage;
